import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Eye, Download, Shield, Mail, Printer, ChevronDown, FileSignature, CheckCircle2, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { POLICIES, POLICY_CATEGORIES, POLICIES_INTRO, findPolicy } from "@/data/policies";
import { trackEvent, attachCampaign } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders one ### heading + its content as an accordion row.
const FaqItem = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10 last:border-b-0" data-testid={`policy-faq-${q.slice(0, 30).toLowerCase().replace(/\s+/g, "-")}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-4 py-4 text-left group"
      >
        <span className="text-sm sm:text-base font-bold text-white leading-snug group-hover:text-brand-yellow transition-colors">{q}</span>
        <ChevronDown
          size={18}
          className={`text-brand-yellow flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="pb-5 pr-7 text-sm text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: a }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Markdown-lite renderer: handles ## headings, bullet lists (- / *),
// numbered lists, paragraphs and **bold**. Also handles the common case
// of an intro paragraph immediately followed by a bullet list inside the
// same block (no blank line between them).
const renderBlock = (block, i) => {
  if (block.startsWith("## ")) {
    return (
      <h3 key={i} className="font-display text-xl sm:text-2xl text-brand-yellow tracking-wide mt-8 mb-3">
        {block.replace(/^##\s+/, "")}
      </h3>
    );
  }

  const lines = block.split("\n");
  const isBullet = (l) => /^[-*]\s+/.test(l);
  const isNumber = (l) => /^\d+\.\s+/.test(l);
  const stripBold = (s) => s.replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>");

  // Walk the block linearly and emit paragraph / ul / ol chunks in order.
  const chunks = [];
  let buf = [];
  let mode = "p"; // "p" | "ul" | "ol"

  const flush = () => {
    if (buf.length === 0) return;
    const idx = chunks.length;
    if (mode === "ul") {
      chunks.push(
        <ul key={`${i}-${idx}`} className="list-disc pl-5 space-y-1.5 text-zinc-300 leading-relaxed">
          {buf.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: stripBold(it.replace(/^[-*]\s+/, "")) }} />
          ))}
        </ul>
      );
    } else if (mode === "ol") {
      chunks.push(
        <ol key={`${i}-${idx}`} className="list-decimal pl-5 space-y-1.5 text-zinc-300 leading-relaxed">
          {buf.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: stripBold(it.replace(/^\d+\.\s+/, "")) }} />
          ))}
        </ol>
      );
    } else {
      chunks.push(
        <p
          key={`${i}-${idx}`}
          className="text-zinc-300 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: stripBold(buf.join(" ")) }}
        />
      );
    }
    buf = [];
  };

  lines.forEach((line) => {
    const next = isBullet(line) ? "ul" : isNumber(line) ? "ol" : "p";
    if (next !== mode) {
      flush();
      mode = next;
    }
    buf.push(line);
  });
  flush();

  return chunks.length === 1 ? chunks[0] : <div key={i} className="space-y-3">{chunks}</div>;
};

const renderPolicyBody = (body) => {
  // Force a blank line after every `## Heading` so the renderer treats the
  // heading and its body as separate blocks even when the source omits it.
  const normalised = body.replace(/^(##\s+.+)\n(?!\n)/gm, "$1\n\n");
  return normalised.split(/\n\n+/).map((block, i) => renderBlock(block, i));
};

// Splits a policy body into { main, faqItems, acknowledgement } so each chunk
// can be styled with its own premium presentation. Returns the original body
// untouched when neither marker is found.
const splitPolicyBody = (body) => {
  const norm = body.replace(/^(##\s+.+)\n(?!\n)/gm, "$1\n\n");
  const ackIdx = norm.search(/^##\s+Acknowledgement\b/im);
  const faqIdx = norm.search(/^##\s+FAQ\b/im);
  let main = norm;
  let faqRaw = "";
  let ackRaw = "";
  if (ackIdx > -1 && (faqIdx === -1 || ackIdx > faqIdx)) {
    ackRaw = norm.slice(ackIdx);
    main = norm.slice(0, ackIdx);
  }
  if (faqIdx > -1) {
    const sliceEnd = ackIdx > -1 ? ackIdx : main.length;
    faqRaw = norm.slice(faqIdx, sliceEnd);
    main = norm.slice(0, faqIdx) + (ackIdx > -1 ? "" : "");
  }
  // Parse FAQ items: each ### heading + following body until the next ###
  const faqItems = [];
  if (faqRaw) {
    // Drop the leading "## FAQ — Frequently Asked Questions" + any intro
    const afterHeading = faqRaw.replace(/^##\s+.+\n+/, "");
    const parts = afterHeading.split(/\n(?=###\s+)/);
    parts.forEach((p) => {
      const m = p.match(/^###\s+(.+)\n+([\s\S]+)$/);
      if (m) {
        const q = m[1].trim();
        // Render the answer with the same markdown-lite logic for bullets / bold
        const aHtml = m[2].trim()
          .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
          .replace(/\n/g, " ");
        faqItems.push({ q, a: aHtml });
      }
    });
  }
  return { main, faqItems, ackRaw };
};

const AcknowledgementForm = ({ policy }) => {
  const [form, setForm] = useState({ full_name: "", email: "", role: "Freelance teacher", agree: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please add your full name and email.");
      return;
    }
    if (!form.agree) {
      toast.error("Please tick the acknowledgement checkbox.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/policies/acknowledge`, attachCampaign({
        policy_slug: policy.slug, ...form,
      }));
      trackEvent("policy_acknowledged", { policy_slug: policy.slug, role: form.role });
      setDone(true);
      toast.success("Acknowledgement received — thank you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record acknowledgement. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="policy-ack-thanks"
        className="rounded-2xl border border-brand-yellow/40 bg-gradient-to-br from-brand-purpleDeep/30 via-zinc-950 to-black p-7 sm:p-9 text-center no-print"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 mb-4">
          <CheckCircle2 size={20} className="text-brand-yellow" />
        </div>
        <h4 className="font-display text-2xl text-white tracking-wide">Acknowledgement recorded.</h4>
        <p className="text-sm text-zinc-400 mt-2">Thank you {form.full_name.split(" ")[0]}. A copy of this acknowledgement has been logged against your name and the policy.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="policy-ack-form"
      className="rounded-2xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/20 via-zinc-950 to-black p-7 sm:p-9 no-print space-y-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <FileSignature size={16} className="text-brand-yellow" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow font-black">Sign &amp; acknowledge</span>
      </div>
      <h4 className="font-display text-2xl sm:text-3xl text-white tracking-wide leading-tight">Acknowledge this policy</h4>
      <p className="text-sm text-zinc-400">Required for freelance onboarding. By submitting, you confirm you've read and understood this policy.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">Full name *</div>
          <input type="text" required value={form.full_name} onChange={update("full_name")} data-testid="policy-ack-name" className="usdd-input" placeholder="Your full legal name" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">Email *</div>
          <input type="email" required value={form.email} onChange={update("email")} data-testid="policy-ack-email" className="usdd-input" placeholder="you@example.com" />
        </label>
      </div>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">Role</div>
        <select value={form.role} onChange={update("role")} data-testid="policy-ack-role" className="usdd-input cursor-pointer">
          {["Freelance teacher", "Freelance choreographer", "Freelance workshop leader", "Freelance performer", "Partner organisation", "Other"].map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </label>

      <label className="flex items-start gap-3 cursor-pointer select-none pt-1" data-testid="policy-ack-checkbox">
        <input
          type="checkbox"
          checked={form.agree}
          onChange={(e) => setForm({ ...form, agree: e.target.checked })}
          data-testid="policy-ack-checkbox-input"
          className="mt-1 h-4 w-4 rounded border-white/30 bg-white/10 text-brand-yellow focus:ring-brand-yellow"
        />
        <span className="text-xs text-zinc-300 leading-relaxed">
          <span className="font-bold text-white">I have read, understood and agree to comply with this policy</span> in connection with my engagement with USDD Academy (operated by USDD Networks).
        </span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        data-testid="policy-ack-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-yellow text-black font-black uppercase text-xs sm:text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.01] transition-all glow-yellow disabled:opacity-60"
      >
        {submitting ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
        Submit acknowledgement
      </button>
    </form>
  );
};

const PolicyCard = ({ p }) => {
  const Icon = p.icon;
  return (
    <motion.div
      data-testid={`policy-card-${p.slug}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35 }}
      className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] hover:border-brand-yellow/40 hover:bg-white/[0.05] transition-all p-5 sm:p-6"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center flex-shrink-0">
          <Icon size={18} className="text-brand-yellow" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.3em] text-brand-purpleLight font-bold mb-1 flex items-center gap-2 flex-wrap">
            {p.category}
            {p.acknowledgement && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 text-brand-yellow text-[8px]">
                <FileSignature size={8} /> Acknowledgement
              </span>
            )}
          </div>
          <h3 className="font-display text-lg sm:text-xl text-white tracking-wide leading-tight">{p.title}</h3>
          {p.audience && (
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1.5">{p.audience}</div>
          )}
        </div>
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed flex-1">{p.summary}</p>
      <div className="mt-5 flex items-center gap-2">
        <Link
          to={`/policies/${p.slug}`}
          data-testid={`policy-view-${p.slug}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-yellow text-black text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover transition-all"
        >
          <Eye size={11} /> View Policy
        </Link>
        <Link
          to={`/policies/${p.slug}?print=1`}
          data-testid={`policy-download-${p.slug}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-zinc-200 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
        >
          <Download size={11} /> Download PDF
        </Link>
      </div>
    </motion.div>
  );
};

export default function PoliciesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");

  usePageMeta(
    "Policies & Safeguarding · USDD Academy",
    "USDD Academy policies — safeguarding, child protection, health & safety, GDPR, codes of conduct, refunds and terms. Reviewed annually."
  );

  const filtered = useMemo(
    () => (category === "all" ? POLICIES : POLICIES.filter((p) => p.category === category)),
    [category]
  );

  // ───────── Detail view: /policies/:slug ─────────
  if (slug) {
    const policy = findPolicy(slug);
    if (!policy) {
      return (
        <>
          <Header />
          <main className="min-h-screen bg-black pt-28 pb-20 flex items-center justify-center">
            <div className="text-center">
              <h1 className="font-display text-3xl text-white mb-4">Policy not found</h1>
              <Link to="/policies" className="text-brand-yellow underline" data-testid="policy-back-to-index">Back to all policies</Link>
            </div>
          </main>
          <Footer />
        </>
      );
    }
    const Icon = policy.icon;
    return (
      <>
        <Header />
        <main className="min-h-screen bg-black pt-28 pb-20">
          {/* Print stylesheet — produces a clean black-on-white PDF when the user prints */}
          <style>{`
            @media print {
              header, footer, .no-print { display: none !important; }
              body { background: white !important; }
              main { padding: 0 !important; }
              .print-wrap { color: black !important; background: white !important; }
              .print-wrap * { color: black !important; }
              .print-wrap h1, .print-wrap h2, .print-wrap h3 { color: #1a1a1a !important; }
              .print-wrap a { color: #4a3aff !important; text-decoration: underline; }
            }
          `}</style>
          <article className="print-wrap max-w-3xl mx-auto px-6 sm:px-8">
            <div className="mb-6 no-print">
              <button
                onClick={() => navigate("/policies")}
                data-testid="policy-back-btn"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-brand-yellow transition-colors"
              >
                <ArrowLeft size={14} /> All Policies
              </button>
            </div>

            <header className="border-b border-white/10 pb-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center flex-shrink-0">
                  <Icon size={22} className="text-brand-yellow" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand-purpleLight font-bold mb-1">{policy.category}</div>
                  <h1 data-testid="policy-detail-title" className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-tight">
                    {policy.title}
                  </h1>
                  <p className="text-xs text-zinc-500 mt-2">
                    {POLICIES_INTRO.contact.serviceName} · operated by {POLICIES_INTRO.contact.legalEntity} ·{" "}
                    <a href={`mailto:${POLICIES_INTRO.contact.email}`} className="text-brand-yellow/80 hover:text-brand-yellow hover:underline">
                      {POLICIES_INTRO.contact.email}
                    </a>
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2 no-print">
                <button
                  onClick={() => window.print()}
                  data-testid="policy-print-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-yellow text-black text-[11px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover transition-all"
                >
                  <Printer size={11} /> Download / Print PDF
                </button>
                <a
                  href={`mailto:${POLICIES_INTRO.contact.email}?subject=Policy%20enquiry%20-%20${encodeURIComponent(policy.title)}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-zinc-200 text-[11px] font-bold uppercase tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
                >
                  <Mail size={11} /> Email USDD Networks
                </a>
              </div>
            </header>

            <div
              data-testid="policy-applicability"
              className="mb-6 px-5 py-4 rounded-2xl border border-brand-purpleLight/25 bg-brand-purpleDeep/15 text-xs sm:text-sm text-zinc-300"
            >
              <span className="inline-block text-[10px] uppercase tracking-[0.28em] text-brand-purpleLight font-bold mr-2">Applicability:</span>
              This policy applies to activities delivered by{" "}
              <strong className="text-white">{POLICIES_INTRO.contact.serviceName}</strong>, operated by{" "}
              <strong className="text-white">{POLICIES_INTRO.contact.legalEntity}</strong>.
            </div>

            <section data-testid="policy-detail-body" className="prose-policy space-y-3">
              {(() => {
                const { main, faqItems, ackRaw } = splitPolicyBody(policy.body);
                return (
                  <>
                    {renderPolicyBody(main)}
                    {faqItems.length > 0 && (
                      <div
                        data-testid="policy-faq-block"
                        className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <span className="h-px w-8 bg-brand-yellow" />
                          <span className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow font-black">Frequently Asked</span>
                        </div>
                        <h3 className="font-display text-2xl sm:text-3xl text-white tracking-wide mb-4">Quick answers.</h3>
                        <div>
                          {faqItems.map((it, idx) => (
                            <FaqItem key={it.q} q={it.q} a={it.a} defaultOpen={idx === 0} />
                          ))}
                        </div>
                      </div>
                    )}
                    {ackRaw && renderPolicyBody(ackRaw)}
                  </>
                );
              })()}
            </section>

            {policy.acknowledgement && (
              <div className="mt-10">
                <AcknowledgementForm policy={policy} />
              </div>
            )}

            <div className="mt-12 pt-6 border-t border-white/10 text-[11px] uppercase tracking-[0.25em] text-zinc-500 no-print">
              For enquiries relating to this policy: <a href={`mailto:${POLICIES_INTRO.contact.email}`} className="text-brand-yellow hover:underline">{POLICIES_INTRO.contact.email}</a>
            </div>
          </article>
        </main>
        <Footer />
        <PrintTrigger />
      </>
    );
  }

  // ───────── Index view: /policies ─────────
  return (
    <>
      <Header />
      <main className="min-h-screen bg-black pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield size={18} className="text-brand-yellow" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.32em] text-brand-yellow">
                {POLICIES_INTRO.eyebrow}
              </span>
            </div>
            <h1 data-testid="policies-title" className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-wide leading-[0.95]">
              {POLICIES_INTRO.title} <span className="text-brand-yellow text-glow-yellow">{POLICIES_INTRO.highlight}</span>
            </h1>
            <p className="mt-5 max-w-3xl text-base sm:text-lg text-zinc-300 leading-relaxed">
              {POLICIES_INTRO.description}
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
              <div className="px-4 py-3 rounded-2xl border border-brand-purpleLight/30 bg-brand-purpleDeep/15 text-xs sm:text-sm">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-purpleLight font-bold mb-1">
                  Safeguarding Contacts
                </div>
                <div className="text-zinc-400 text-[11px] mb-2">For child protection and safeguarding matters only.</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-purpleLight/80 font-bold">
                  {POLICIES_INTRO.contact.dsl.label}
                </div>
                <div className="text-white font-bold">{POLICIES_INTRO.contact.dsl.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-purpleLight/80 mt-2 font-bold">
                  {POLICIES_INTRO.contact.deputy.label}
                </div>
                <div className="text-white">{POLICIES_INTRO.contact.deputy.name}</div>
                <a
                  href={`mailto:${POLICIES_INTRO.contact.email}`}
                  data-testid="policies-dsl-email"
                  className="mt-3 inline-block text-brand-yellow hover:underline font-bold"
                >
                  {POLICIES_INTRO.contact.email}
                </a>
              </div>
              <div className="px-4 py-3 rounded-2xl border border-brand-yellow/30 bg-brand-yellow/[0.04] text-xs sm:text-sm">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-yellow font-bold mb-1">
                  {POLICIES_INTRO.contact.bookings.label}
                </div>
                <p className="text-zinc-300 mb-2">
                  For all refund, cancellation or booking enquiries, please contact:
                </p>
                <a
                  href={`mailto:${POLICIES_INTRO.contact.bookings.email}`}
                  data-testid="policies-bookings-email"
                  className="text-brand-yellow hover:underline font-bold"
                >
                  {POLICIES_INTRO.contact.bookings.email}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="policies-filters">
            {POLICY_CATEGORIES.map((c) => {
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`policies-filter-${c.id.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3.5 py-1.5 rounded-full text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] transition-all ${
                    active
                      ? "bg-brand-yellow text-black glow-yellow"
                      : "bg-white/[0.04] border border-white/15 text-zinc-300 hover:border-brand-yellow/50 hover:text-brand-yellow"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="policies-grid">
            {filtered.map((p) => (
              <PolicyCard key={p.slug} p={p} />
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
              <div className="lg:col-span-2">
                <h3 className="font-display text-xl text-white tracking-wide mb-1">Something missing or unclear?</h3>
                <p className="text-sm text-zinc-400">
                  We're committed to transparency. If you'd like a printable bundle of all policies, a school-specific safeguarding statement or to raise a concern, get in touch.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`mailto:${POLICIES_INTRO.contact.email}?subject=Policy%20enquiry`}
                  data-testid="policies-contact-dsl"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-wider hover:bg-brand-yellowHover transition-all glow-yellow"
                >
                  <Mail size={14} /> Contact DSL
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-xs tracking-wider hover:bg-white hover:text-black transition-all"
                >
                  General contact <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

// Tiny helper component: when the URL has ?print=1 we trigger the browser
// print dialog once after render. This is what powers the "Download PDF"
// button on the index — most browsers offer "Save as PDF" inside print.
function PrintTrigger() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("print") !== "1") return;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, []);
  return null;
}
